import { useState } from "react";
import { Copy, Check, LogOut, Save, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { meOf } from "@/lib/couple";
import { TIMEZONES } from "@/lib/timezones";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export default function Settings({ couple, slot, refreshCouple, onLogout }) {
  const me = meOf(couple, slot);
  const [title, setTitle] = useState(couple.couple_title || "");
  const [since, setSince] = useState(couple.together_since || "");
  const [reunion, setReunion] = useState(couple.reunion_date || "");
  const [city, setCity] = useState(me.city || "");
  const [tz, setTz] = useState(me.timezone || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const save = async () => {
    setBusy(true);
    const payload = {
      couple_title: title || null,
      together_since: since || null,
      reunion_date: reunion || null,
    };
    if (slot === "a") { payload.partner_a_city = city; payload.partner_a_timezone = tz; }
    else { payload.partner_b_city = city; payload.partner_b_timezone = tz; }
    try {
      await api.patch(`/couples/${couple.id}`, payload);
      toast.success("Saved 💾");
      refreshCouple();
    } catch {
      toast.error("Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(couple.join_code);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-primary">Settings</div>
        <h1 className="font-serif text-3xl font-semibold">You &amp; your space</h1>
      </div>

      {/* invite code */}
      {!couple.connected && (
        <div className="rounded-3xl bg-gradient-to-br from-accent/60 to-card border border-border/70 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4 text-primary" />
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">Invite your partner</span>
          </div>
          <div className="flex items-center justify-between">
            <span data-testid="settings-join-code" className="font-mono text-3xl font-bold tracking-[0.25em] text-primary">{couple.join_code}</span>
            <Button data-testid="settings-copy-code" size="sm" variant="outline" onClick={copyCode} className="rounded-full">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-card border border-border/70 p-5 space-y-4">
        <Field label="Couple name">
          <Input data-testid="settings-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl h-11" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Together since">
            <Input data-testid="settings-since-input" type="date" value={since} onChange={(e) => setSince(e.target.value)} className="rounded-xl h-11" />
          </Field>
          <Field label="Next reunion">
            <Input data-testid="settings-reunion-input" type="date" value={reunion} onChange={(e) => setReunion(e.target.value)} className="rounded-xl h-11" />
          </Field>
        </div>
      </div>

      <div className="rounded-3xl bg-card border border-border/70 p-5 space-y-4">
        <div className="text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">Your location ({me.name})</div>
        <Field label="Your city">
          <Input data-testid="settings-city-input" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-xl h-11" />
        </Field>
        <Field label="Your time zone">
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="settings-tz-select" className="rounded-xl h-11"><SelectValue placeholder="Time zone" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Button data-testid="settings-save-btn" disabled={busy} onClick={save} className="w-full rounded-full h-12 font-semibold gap-1">
        <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
      </Button>

      <Button data-testid="logout-btn" variant="ghost" onClick={onLogout} className="w-full rounded-full text-muted-foreground gap-1">
        <LogOut className="h-4 w-4" /> Log out
      </Button>
    </div>
  );
}
